import { CircleHelp, FolderOpen, Home, Settings, Users, type LucideIcon } from "lucide-react";

import {
  CLUSTER_ORDER,
  CLUSTER_META,
  clusterRoute,
  type CapabilityCluster,
} from "@/entities/capability";
import {
  dispatchItems,
  helpItems,
  orgItems,
  personalItems,
  worksAndAssetItems,
  type NavItem,
} from "@/widgets/appSidebarNav";

/**
 * 租户端外壳导航模型(74px 图标轨 + 上下文面板)——**纯数据 / 解析**,可单测。
 *
 * ★两栏壳(轨=选群,面板=只列该群舱,收 17 舱复杂度)。轨项 = 首页 + 5 能力群 + 3 支撑区(资产/组织/设置)。
 * ★能力群的舱是**动态**的(/me/capabilities + dept),不在这;这里只管轨的**结构**与「当前路由属哪一轨项」。
 * 平台超管 scope 不走本壳(沿用 AppSidebar 平台树),故本模型只表达租户轨。
 */
export type RailKey = "home" | CapabilityCluster | "assets" | "org" | "personal" | "help";

/**
 * 能力群 → 该群已开舱的路由前缀(即将/融入舱无路由,不在此)。用于「当前路由高亮哪个群」。
 *
 * ★情报决策群(V25 军师回归)四条要**逐条列全**:解析用 `path === url || startsWith(url + "/")`
 *   带边界,"/advisor" 不会顺带命中 "/advisor-team-board" —— 漏一条就是「点卡进去,轨的高亮
 *   掉回首页」的分叉。/advisor/board 是中转页,落地在下面两条上,不必单列。
 */
export const CLUSTER_ROUTES: Record<CapabilityCluster, string[]> = {
  content: ["/ai-haibao", "/ai-video", "/ai-digital-human", "/ai-explosive", "/ai-jizhe"],
  acquisition: ["/geo-radar", "/ai-huoke", "/ai-promotion", "/ai-website", "/ai-official-media"],
  conversion: ["/ai-kefu", "/ai-peilian"],
  intelligence: ["/biz-advisor", "/advisor", "/advisor-team-board", "/growth-cockpit"],
  operations: [],
};

/**
 * 支撑区轨项的 key —— **单源**。轨项顺序、解析表、`goRail` 的「支撑区 → 本区第一页」三处都读它。
 * 0919 加「帮助」时发现 `TenantShellNav.goRail` 是手写的三项字面量或判断,漏改就是
 *「点帮助图标什么都不发生」—— 所以把这一串收成常量,新增支撑区只改这一行。
 */
export const SUPPORT_RAIL_KEYS = ["assets", "org", "personal", "help"] as const;
export type SupportRailKey = (typeof SUPPORT_RAIL_KEYS)[number];

/**
 * 支撑区轨判据(类型守卫)。★用它而不是 `SUPPORT_RAIL_KEYS.includes(key)`:后者不收窄类型,
 * 剩下的分支里 `key` 仍是整个 RailKey,喂给 `clusterRoute(cluster: CapabilityCluster)` 就是
 * TS2345 —— 0919 加「帮助」轨那笔正是这样把 console main 的 `npm run typecheck` 打红的
 * (worktree 里跑 `npx tsc --noEmit` 走的不是 `tsc -b`,没抓到)。
 */
export function isSupportRailKey(key: RailKey): key is SupportRailKey {
  return (SUPPORT_RAIL_KEYS as readonly string[]).includes(key);
}

/**
 * 支撑区面板分组(复用 appSidebarNav 单源,gate 逐字保留)。
 *
 * ★组织区尾部接派单闭环(团队任务 / 派单回看):派活是**人和任务**的事,不是一项 AI 能力。
 *   接在尾部而不是插在前面 —— 轨的 goRail 拿首项当默认落点(ShellContextStrip 只画条),插前面会把「组织」轨的默认页
 *   从用户管理改成团队任务(顺手改掉别人的默认落点,是不该顺手做的事)。
 *   不并进 `orgItems` 本身:那份也喂平台树,并进去会让平台超管的「组织」组冒出租户派单项。
 */
export const SUPPORT_GROUPS: Record<SupportRailKey, NavItem[]> = {
  assets: worksAndAssetItems,
  org: [...orgItems, ...dispatchItems],
  personal: personalItems,
  help: helpItems,
};

export interface RailSection {
  key: RailKey;
  /** 面板头 / aria 用的全名。 */
  labelKey: string;
  /** 74px 轨上 9px 小字用的短名(群 = cluster.<c>Short)。 */
  railLabelKey: string;
  icon: LucideIcon;
  /** cluster = 能力群(面板列该群舱);support = 支撑区(面板列 NavItem 组);home = 直达首页,无面板。 */
  kind: "home" | "cluster" | "support";
}

/** 轨项顺序 = 首页 → 5 能力群 → 资产 / 组织 / 设置(mock 布局)。 */
export const RAIL_SECTIONS: RailSection[] = [
  { key: "home", labelKey: "shell.home", railLabelKey: "shell.home", icon: Home, kind: "home" },
  ...CLUSTER_ORDER.map(
    (c): RailSection => ({
      key: c,
      labelKey: CLUSTER_META[c].labelKey,
      railLabelKey: `${CLUSTER_META[c].labelKey}Short`,
      icon: CLUSTER_META[c].railIcon,
      kind: "cluster",
    })
  ),
  {
    key: "assets",
    labelKey: "shell.assets",
    railLabelKey: "shell.assets",
    icon: FolderOpen,
    kind: "support",
  },
  { key: "org", labelKey: "shell.org", railLabelKey: "shell.org", icon: Users, kind: "support" },
  {
    key: "personal",
    labelKey: "shell.personal",
    railLabelKey: "shell.personal",
    icon: Settings,
    kind: "support",
  },
  // 帮助(2026-09-19 §21.1):支撑区第 4 项。放在最后 —— 它是「不会用了才来」的地方,
  // 不该排在每天要用的资产 / 组织 / 设置之前。
  {
    key: "help",
    labelKey: "shell.help",
    railLabelKey: "shell.help",
    icon: CircleHelp,
    kind: "support",
  },
];

/**
 * 归「首页」的路由(1004 T11)。军师满屏对话页 `/advisor/chat` 是首页 hero / 悬浮球「展开整页」的落点,
 * 不是情报决策群里的「经营看板」—— 但它挂在 `/advisor` 前缀下,不单列就会被解析成情报决策群,
 * 顶栏条上还会把「经营看板」点亮(`/advisor/chat` 以 `/advisor/` 开头),STAFF 看到一个自己进不去的群高亮。
 * 比 `/advisor` 长,按最长前缀先命中,压过群前缀。
 */
export const HOME_ROUTES = ["/advisor/chat"];

// 解析表:cluster 路由 + 支撑区 NavItem url,按 url 长度降序 —— 最长前缀先命中
// (历史例:/ai-digital-human/my-assets 属「资产」压过「内容」;W2 起该路由重定向 /characters)。
const RESOLUTION_TABLE: { url: string; key: RailKey }[] = [
  // 群页本身(/g/<cluster>):点轨上群图标的落点 —— 漏了它,一点群图标高亮就掉回「首页」
  // (2026-08-09 用户实测报障:「左边的 bar 没有根据我的选中高亮」,根因就是这五条不在表里)。
  ...HOME_ROUTES.map((url) => ({ url, key: "home" as RailKey })),
  ...CLUSTER_ORDER.map((c) => ({ url: clusterRoute(c), key: c as RailKey })),
  ...CLUSTER_ORDER.flatMap((c) => CLUSTER_ROUTES[c].map((url) => ({ url, key: c as RailKey }))),
  ...SUPPORT_RAIL_KEYS.flatMap((k) =>
    SUPPORT_GROUPS[k].map((it) => ({ url: it.url, key: k as RailKey }))
  ),
].sort((a, b) => b.url.length - a.url.length);

/**
 * 群 → 任务语言副标的 i18n key(2026-07-30,7.29 反馈任务 18);非能力群返 undefined。
 *
 * ★为什么要它:一级导航六项字号权重完全一致、且用的是**营销漏斗术语**(内容生产 / 获客推广)
 * 而不是任务语言 —— 新手想的是「我要做张海报」,映射不过去。副标(cluster.*Sub)已改成
 * 「做图 / 做视频 / 写文案 / 拆爆款」这类**能对上用户嘴里那句话**的写法,轨上挂 title、
 * 群面板头里显示。★不动 IA 骨架:群还是那五群,顺序一个字没改。
 */
export function subKeyOf(key: RailKey): string | undefined {
  return key in CLUSTER_META ? CLUSTER_META[key as CapabilityCluster].subKey : undefined;
}

/** 当前路由属哪个轨项(高亮 + 初始面板)。无匹配 → home。 */
export function resolveRailKey(path: string): RailKey {
  for (const { url, key } of RESOLUTION_TABLE) {
    if (path === url || path.startsWith(url + "/")) return key;
  }
  return "home";
}
